import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency, formatCurrencyPrecise, priceBreakdown, formatDate } from '../lib/format';
import { X, ShieldCheck, User, Phone, Mail, MapPin, ArrowRight, Lock } from 'lucide-react';

/** Who is in the room. Asked on the form so the mentor knows the mix before
 *  day one, and so the admin console can see which audience an ad actually
 *  brought in. */
const PROFESSIONS = [
  'Student',
  'Job',
  'Homemaker',
  'Businessman',
  'Job Seekers',
  'Teachers',
  'Coaches',
  'Others',
];

/** Field-level validation. Returns a message, or null when the value is fine. */
const VALIDATORS = {
  studentName: (value) =>
    value.trim().length < 3 ? 'Please enter your full name.' : null,
  phone: (value) => {
    const digits = value.replace(/\D/g, '');
    // Indian mobile numbers are 10 digits, optionally prefixed with 91.
    if (digits.length < 10) return 'Enter a 10-digit mobile number.';
    if (digits.length > 12) return 'That number looks too long.';
    return null;
  },
  email: (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim()) ? null : 'Enter a valid email address.',
  city: (value) => (value.trim() ? null : 'Tell us your city.'),
  profession: (value) => (value ? null : 'Pick the one that fits you best.'),
};

export const RegistrationModal = () => {
  const { selectedWorkshop, startCheckout, closeModal } = useApp();

  const [form, setForm] = useState({
    studentName: '',
    phone: '',
    email: '',
    city: '',
    profession: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && closeModal();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeModal]);

  /* ── Keep the sheet above the software keyboard ────────────────────────────
     A phone keyboard shrinks the *visual* viewport but usually leaves the
     layout viewport alone, so a `position: fixed` overlay keeps its full
     height and the keyboard sits over the bottom of the form — which is where
     the remaining fields and the pay button are. Pinning the overlay to the
     region that is actually on screen puts the sheet back above it. */
  useEffect(() => {
    const viewport = window.visualViewport;
    const overlay = overlayRef.current;
    if (!viewport || !overlay) return undefined;

    const fit = () => {
      overlay.style.top = `${viewport.offsetTop}px`;
      overlay.style.height = `${viewport.height}px`;
      overlay.style.bottom = 'auto';
    };

    fit();
    viewport.addEventListener('resize', fit);
    viewport.addEventListener('scroll', fit);
    return () => {
      viewport.removeEventListener('resize', fit);
      viewport.removeEventListener('scroll', fit);
    };
  }, []);

  /* Autofocus is deliberately not done here. On a phone it opens the keyboard
     the instant the sheet appears, over a form the visitor has not read yet. */

  /** Bring the field being filled into view, above the keyboard. */
  const revealField = (event) => {
    const field = event.currentTarget;
    setTimeout(() => {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 250); // after the keyboard animation, or it scrolls to the old geometry
  };

  if (!selectedWorkshop) return null;

  const basePrice = selectedWorkshop.price;
  const { net, gst, total } = priceBreakdown(basePrice);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: VALIDATORS[name]?.(value) ?? null }));
    }
  };

  const blurField = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: VALIDATORS[name]?.(form[name]) ?? null }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {};
    for (const [name, validate] of Object.entries(VALIDATORS)) {
      const message = validate(form[name]);
      if (message) nextErrors[name] = message;
    }

    setErrors(nextErrors);
    setTouched(Object.fromEntries(Object.keys(VALIDATORS).map((key) => [key, true])));

    if (Object.keys(nextErrors).length) {
      document.querySelector('[data-invalid="true"]')?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await startCheckout({
        ...form,
        amount: net,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Phone first: it is the field that matters most (WhatsApp delivery) and the
  // one people abandon on, so it sits where attention is highest.
  const fields = [
    { name: 'studentName', label: 'Full name', icon: User, type: 'text', placeholder: 'Rahul Sharma', autoComplete: 'name' },
    { name: 'phone', label: 'WhatsApp number', icon: Phone, type: 'tel', placeholder: '98765 43210', autoComplete: 'tel', inputMode: 'tel' },
    { name: 'email', label: 'Email address', icon: Mail, type: 'email', placeholder: 'rahul@example.com', autoComplete: 'email' },
    { name: 'city', label: 'City', icon: MapPin, type: 'text', placeholder: 'Kolkata', autoComplete: 'address-level2' },
  ];

  return (
    <div
      ref={overlayRef}
      className="sheet-overlay animate-fade-in"
      onClick={closeModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="registration-title"
    >
      <div onClick={(event) => event.stopPropagation()} className="sheet animate-sheet-up">
        <div className="sheet-handle" />

        {/* Header */}
        <div className="px-5 py-4 border-b border-ink-200 flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <span className="pill pill-brand mb-2">Step 1 of 2 · Your details</span>
            <h2 id="registration-title" className="font-display text-lg font-bold text-ink-900">
              Reserve your seat
            </h2>
          </div>
          <button
            onClick={closeModal}
            aria-label="Close"
            className="p-2 -mr-1 rounded-full text-ink-400 hover:text-ink-900 hover:bg-ink-100 transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Chosen workshop */}
        <div className="px-5 py-3 bg-ink-50 border-b border-ink-200 flex items-center gap-3 shrink-0">
          <img
            src={selectedWorkshop.banner}
            alt=""
            className="w-11 h-11 rounded-xl object-cover shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-ink-900 truncate">{selectedWorkshop.title}</p>
            <p className="text-[11px] text-ink-500">
              {formatDate(selectedWorkshop.date)} · {selectedWorkshop.time}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="sheet-body p-5 space-y-5 safe-bottom">
          <div className="grid grid-cols-1 gap-4">
            {fields.map(({ name, label, icon: Icon, type, placeholder, autoComplete, inputMode }) => {
              const invalid = Boolean(errors[name]);
              return (
                <div key={name}>
                  <label htmlFor={`reg-${name}`} className="field-label">
                    {label} <span className="text-brand-600">*</span>
                  </label>
                  <div className="relative">
                    <Icon className="w-4 h-4 text-ink-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      id={`reg-${name}`}
                      type={type}
                      inputMode={inputMode}
                      autoComplete={autoComplete}
                      placeholder={placeholder}
                      value={form[name]}
                      onChange={(event) => setField(name, event.target.value)}
                      onFocus={revealField}
                      onBlur={() => blurField(name)}
                      aria-invalid={invalid}
                      aria-describedby={invalid ? `reg-${name}-error` : undefined}
                      data-invalid={invalid}
                      className={`field field-icon ${invalid ? 'field-error' : ''}`}
                    />
                  </div>
                  {invalid && (
                    <p id={`reg-${name}-error`} className="mt-1 text-[11px] font-medium text-brand-600">
                      {errors[name]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Profession. A radio group, not a select: eight short options are
              faster to tap than a picker wheel, and every choice stays visible
              so nobody has to open a menu to find out what is on offer. */}
          <fieldset>
            <legend className="field-label">
              Profession <span className="text-brand-600">*</span>
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {PROFESSIONS.map((option, index) => {
                const selected = form.profession === option;
                return (
                  <label key={option} className="cursor-pointer">
                    <input
                      type="radio"
                      name="profession"
                      value={option}
                      checked={selected}
                      onChange={() => {
                        setTouched((prev) => ({ ...prev, profession: true }));
                        setField('profession', option);
                        setErrors((prev) => ({ ...prev, profession: null }));
                      }}
                      aria-describedby={errors.profession ? 'reg-profession-error' : undefined}
                      data-invalid={index === 0 ? Boolean(errors.profession) : undefined}
                      className="peer sr-only"
                    />
                    <span
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-[0.78rem] font-semibold transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/40 ${
                        selected
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : `bg-white text-ink-600 ${
                              errors.profession ? 'border-brand-300' : 'border-ink-200'
                            }`
                      }`}
                    >
                      <span
                        className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                          selected ? 'border-brand-600' : 'border-ink-300'
                        }`}
                        aria-hidden="true"
                      >
                        {selected && <span className="w-1.5 h-1.5 rounded-full bg-brand-600" />}
                      </span>
                      {option}
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.profession && (
              <p id="reg-profession-error" className="mt-1 text-[11px] font-medium text-brand-600">
                {errors.profession}
              </p>
            )}
          </fieldset>

          {/* Price */}
          <div className="rounded-2xl border border-ink-200 bg-ink-50 p-4 space-y-2 text-sm">
            <div className="flex justify-between text-ink-600">
              <span>Workshop fee</span>
              <span className="font-medium text-ink-800">{formatCurrency(basePrice)}</span>
            </div>
            <div className="flex justify-between text-ink-600">
              <span>GST (18%)</span>
              <span className="font-medium text-ink-800">{formatCurrencyPrecise(gst)}</span>
            </div>
            <div className="pt-2 mt-1 border-t border-ink-200 flex justify-between items-baseline">
              <span className="font-bold text-ink-900">Total payable</span>
              <span className="text-xl font-bold text-brand-700">{formatCurrencyPrecise(total)}</span>
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-xl bg-pay-500/6 border border-pay-400/20 p-3.5">
            <ShieldCheck className="w-5 h-5 text-pay-500 shrink-0" />
            <p className="text-[11px] text-ink-600 leading-relaxed">
              You will be taken to <strong className="text-ink-900">Cashfree's secure checkout</strong>{' '}
              to pay. Your GST invoice and WhatsApp group link appear the moment payment clears.
            </p>
          </div>

          <button type="submit" disabled={submitting} className="btn btn-primary btn-lg w-full">
            {submitting ? (
              'Setting up payment…'
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Pay {formatCurrencyPrecise(total)} securely
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          <p className="text-[11px] text-ink-400 text-center leading-relaxed">
            By continuing you agree to our terms and refund policy. We never store your card details.
          </p>
        </form>
      </div>
    </div>
  );
};
